/**
 * Visits Tab Component
 * Visit tracking for Volunteer Management
 */

import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, AlertCircle } from "lucide-react";

const VisitsTab = ({
  isDark,
  visits,
  missedVisits,
  setVisitDialog
}) => {
  return (
    <div className="space-y-4">
      {/* Missed Visits Alert */}
      {missedVisits.length > 0 && (
        <Card className={`${isDark ? "bg-slate-900 border-red-500/30" : "bg-red-50 border-red-200"}`}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-red-500">
              <AlertCircle className="w-5 h-5" />
              Missed Visits ({missedVisits.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {missedVisits.map((mv, idx) => (
                <div 
                  key={idx}
                  className={`p-3 rounded-lg flex items-center justify-between ${isDark ? "bg-slate-800" : "bg-white"}`}
                  data-testid={`missed-visit-${idx}`}
                >
                  <div>
                    <p className="font-medium">{mv.volunteer_name} → {mv.patient_name}</p>
                    <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                      Last visit: {mv.last_visit_date || "Never"}
                    </p>
                  </div>
                  <Badge className="bg-red-500/20 text-red-400">
                    {mv.days_since_last_visit ? `${mv.days_since_last_visit} days ago` : "No visits"}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Visit Log */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Visit Log</CardTitle>
            <CardDescription>Recent volunteer visits</CardDescription>
          </div>
          <Button 
            className="bg-teal-600 hover:bg-teal-700"
            onClick={() => setVisitDialog(true)}
            data-testid="log-visit-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Log Visit
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className={isDark ? "border-slate-800" : ""}>
                <TableHead>Date</TableHead>
                <TableHead>Volunteer</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Activities</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visits.map((visit) => (
                <TableRow key={visit.id} className={isDark ? "border-slate-800" : ""} data-testid={`visit-row-${visit.id}`}>
                  <TableCell>{visit.visit_date}</TableCell>
                  <TableCell className="font-medium">{visit.volunteer_name}</TableCell>
                  <TableCell>{visit.patient_id}</TableCell>
                  <TableCell>{visit.duration_minutes} min</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(visit.activities || []).slice(0, 2).map((act, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {act}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className={`max-w-[200px] truncate ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {visit.notes || visit.observations || "-"}
                  </TableCell>
                </TableRow>
              ))}
              {visits.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <p className={isDark ? "text-gray-500" : "text-gray-400"}>
                      No visits logged
                    </p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default VisitsTab;
